import { Flex, Paper, Text, Title, Anchor } from "@mantine/core";
import {
  IconBuilding,
  IconPhone,
  IconMail,
  IconWorld,
  IconExternalLink,
  IconRosetteDiscountCheckFilled,
} from "@tabler/icons-react";
import CompanyProfileHeaderLayout from "@/components/shared/AnonymousCompanyProfile/CompanyProfileHeaderLayout";
import { flagEmojiFromHeadquartersCountry } from "@/shared/utils/countryFlagEmoji";
import { ANONYMIZED_MASK } from "@/shared/utils/anonymizedProfileDisplay";
import { getCompanyWebsiteDisplayLabel } from "@/shared/utils/companyWebsiteDisplay.util";
import { formatCompanyDisplayName } from "@/shared/utils/companyDisplayName.util";
import { useMemo } from "react";

type WorkspacePartnerProfileCardProps = {
  company: {
    gtid?: string | null;
    legalName?: string | null;
    tradeName?: string | null;
    status?: string | null;
    headquartersCity?: string | null;
    headquartersCountry?: string | null;
    website?: string | null;
    phoneCountryCode?: string | null;
    phoneNumber?: string | null;
    email?: string | null;
  };
  /** Mask phone and email (pre–both-paid preview). */
  anonymized?: boolean;
};

function CompanyLogo() {
  return (
    <Flex
      w={48}
      h={48}
      style={{
        borderRadius: 8,
        border: "1px solid #8EA6CF",
        backgroundColor: "#F3F6FC",
        flexShrink: 0,
        overflow: "hidden",
      }}
      align="center"
      justify="center"
    >
      <IconBuilding size={24} color="#6B7280" />
    </Flex>
  );
}

export default function WorkspacePartnerProfileCard({
  company,
  anonymized = true,
}: WorkspacePartnerProfileCardProps) {
  const headquartersFlagEmoji = useMemo(
    () => flagEmojiFromHeadquartersCountry(company?.headquartersCountry),
    [company?.headquartersCountry]
  );

  const companyDisplayName = useMemo(
    () => formatCompanyDisplayName(company?.legalName, company?.tradeName),
    [company?.legalName, company?.tradeName],
  );

  const locationLabel =
    company.headquartersCity && company.headquartersCountry
      ? `${company.headquartersCity}, ${company.headquartersCountry}`
      : company.headquartersCountry || "N/A";

  const phoneLabel = anonymized
    ? ANONYMIZED_MASK
    : company.phoneCountryCode && company.phoneNumber
      ? `${company.phoneCountryCode} ${company.phoneNumber}`
      : "N/A";
  const emailLabel = anonymized ? ANONYMIZED_MASK : company.email || "N/A";

  const websiteNode = anonymized ? (
    <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
      <IconWorld size={14} color="#6B7280" style={{ flexShrink: 0 }} />
      <Text size="sm" c="slate.7">
        {ANONYMIZED_MASK}
      </Text>
    </Flex>
  ) : company.website ? (
    <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
      <IconWorld size={14} color="var(--mantine-color-primary-6)" style={{ flexShrink: 0 }} />
      <Anchor
        href={company.website.startsWith("http") ? company.website : `https://${company.website}`}
        target="_blank"
        rel="noopener noreferrer"
        td="none"
        size="sm"
        c="primary.6"
      >
        <Flex align="center" gap={4} component="span" style={{ minWidth: 0 }}>
          <Text component="span" size="sm" style={{ wordBreak: "break-all" }}>
            {getCompanyWebsiteDisplayLabel(company.website)}
          </Text>
          <IconExternalLink size={12} style={{ flexShrink: 0 }} />
        </Flex>
      </Anchor>
    </Flex>
  ) : (
    <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
      <IconWorld size={14} color="#6B7280" style={{ flexShrink: 0 }} />
      <Text size="sm" c="slate.6">
        N/A
      </Text>
    </Flex>
  );

  const contactRow = (
    <Flex gap="md" wrap="wrap" align="center" w="100%">
      <Flex align="center" gap={8} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        {headquartersFlagEmoji ? (
          <Text component="span" fz={16} lh={1} role="img" aria-hidden>
            {headquartersFlagEmoji}
          </Text>
        ) : null}
        <Text size="sm" c="slate.7">
          {locationLabel}
        </Text>
      </Flex>
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconPhone size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7">
          {phoneLabel}
        </Text>
      </Flex>
      <Flex align="center" gap={6} wrap="nowrap" miw={0} style={{ flexShrink: 0 }}>
        <IconMail size={14} color="#6B7280" style={{ flexShrink: 0 }} />
        <Text size="sm" c="slate.7">
          {emailLabel}
        </Text>
      </Flex>
      {websiteNode}
    </Flex>
  );

  return (
    <Paper
      p="md"
      radius="lg"
      style={{
        border: "1px solid #9CB1D8",
        backgroundColor: "#E9EEFA",
        boxShadow: "0 1px 3px rgba(15, 23, 42, 0.08)",
        maxWidth: "100%",
        overflow: "hidden",
      }}
    >
      <CompanyProfileHeaderLayout
        logo={<CompanyLogo />}
        title={
          <Flex align="center" gap={6} wrap="wrap">
            <Title
              order={2}
              fw={700}
              fz={19}
              c="#04305c"
              style={{ lineHeight: 1.25, wordBreak: "break-word", margin: 0 }}
            >
              {companyDisplayName}
            </Title>
            {company.status === "VERIFIED" && (
              <IconRosetteDiscountCheckFilled size={20} color="var(--mantine-color-primary-6)" />
            )}
          </Flex>
        }
        gtidRow={
          company.gtid ? (
            <Text fz="sm" c="#334155" fw={400} style={{ whiteSpace: "nowrap" }}>
              {company.gtid}
            </Text>
          ) : undefined
        }
        country={contactRow}
      />
    </Paper>
  );
}
